/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/ban-types */
import React from 'react';
import {
  DragDropContext,
  Droppable,
  Draggable,
  DraggableProvided,
  DraggableStateSnapshot,
} from 'react-beautiful-dnd';
import { useDnD, DndParent, DnDItem, UseDnDParams } from './DndList.utils';
import {
  listContainer,
  listDraggingOver,
  itemContainer,
  itemDragging,
} from './DndList.css';
import { F1 } from '@client/new/helpers/types';

type Renderer<T, TExtra extends {} = {}> = F1<
  {
    item: T;
    index: number;
    provided: DraggableProvided;
    snapshot: DraggableStateSnapshot;
  } & TExtra,
  React.ReactNode
>;

export type DnDParentRenderer<T> = Renderer<T, { children?: React.ReactNode }>;
type DnDChildRenderer<T> = Renderer<T>;

type DndListProps<TParent extends DndParent<TChild>, TChild extends DnDItem> = {
  renderParent: DnDParentRenderer<TParent>;
  renderChild: DnDChildRenderer<TChild>;
} & UseDnDParams<TParent, TChild>;

export function DndList<
  TParent extends DndParent<TChild>,
  TChild extends DnDItem,
>(p: DndListProps<TParent, TChild>) {
  const { items = [], handleDragEnd } = useDnD(p);

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Droppable droppableId="droppable" type="droppableItem">
        {(droppableProvided, droppableSnapshot) => (
          <div
            ref={droppableProvided.innerRef}
            {...droppableProvided.droppableProps}
            className={`${listContainer} ${
              droppableSnapshot.isDraggingOver ? listDraggingOver : ''
            }`}
          >
            {items.map((parent, index) => {
              return (
                <Draggable
                  key={parent.id}
                  draggableId={parent.id}
                  index={index}
                >
                  {(draggableProvided, draggableSnapshot) => {
                    const childrenNode = (
                      <Droppable
                        droppableId={parent.id}
                        type="droppableSubItem"
                      >
                        {(childProvided) => (
                          <div
                            ref={childProvided.innerRef}
                            {...childProvided.droppableProps}
                          >
                            {(parent.items || []).map((child, childIndex) => {
                              return (
                                <Draggable
                                  key={child.id}
                                  draggableId={child.id}
                                  index={childIndex}
                                >
                                  {(childProvidedDrag, childSnapshotDrag) => {
                                    return p.renderChild({
                                      item: child,
                                      index: childIndex,
                                      provided: childProvidedDrag,
                                      snapshot: childSnapshotDrag,
                                    }) as any;
                                  }}
                                </Draggable>
                              );
                            })}

                            {childProvided.placeholder}
                          </div>
                        )}
                      </Droppable>
                    );

                    return (
                      <div>
                        <div
                          ref={draggableProvided.innerRef}
                          {...draggableProvided.draggableProps}
                          className={`${itemContainer} ${
                            draggableSnapshot.isDragging ? itemDragging : ''
                          }`}
                          style={draggableProvided.draggableProps.style}
                        >
                          {p.renderParent({
                            item: parent,
                            index,
                            provided: draggableProvided,
                            snapshot: draggableSnapshot,
                            children: childrenNode,
                          })}
                        </div>
                      </div>
                    );
                  }}
                </Draggable>
              );
            })}

            {droppableProvided.placeholder}
          </div>
        )}
      </Droppable>
    </DragDropContext>
  );
}

export const getParentDnDProps = (p: DraggableProvided) => p.dragHandleProps;

export const getChildDnDProps = (p: DraggableProvided) => ({
  ref: p.innerRef,
  ...p.draggableProps,
  ...p.dragHandleProps,
  style: p.draggableProps.style,
});
